import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useCallback, useEffect, useState } from 'react';
import { Check, X, AlertTriangle, ExternalLink, ClipboardList, ChevronDown, Copy,
  ChevronsDownUp, ChevronsUpDown, Lock, RotateCcw,
} from 'lucide-react';
import {
  needsTerminalId, deviceLane, osInstallRequired, CONFIGURED_STAGE_IDS, type DeviceLane,
} from '@platform/shared-types';
// The OS gate, the stamp and the card that draws them are SHARED with the
// Pending configuration list at /pos-config — see UnitProvisioning.tsx. Two
// copies of a safety gate drift the first time one of them changes.
import { lockReason, OsInstallCard } from './UnitProvisioning';
import { stageForSnapshotLabel } from './provisioningStages';
import {
  hardwareApi, type DeviceUnitRow, type DeviceModelRow, type ProvisioningTemplate,
} from '../../services/hardwareApi';
import { toastService } from '../../services/toastService';
import ReinstallModal from './ReinstallModal';
import { useDataVersion } from '../../services/dataChangeBus';

/**
 * HAS A REQUESTED REINSTALL NOT HAPPENED YET?
 *
 * The stamp is never cleared, so presence alone says nothing — a request older
 * than the install was carried out, and treating that as outstanding would
 * have the row telling somebody to redo work already done. The comparison is
 * the same one `osInstallGate` makes server-side.
 */
const outstandingReinstall = (u: DeviceUnitRow): boolean =>
  !!u.reinstall && u.reinstall.requestedAt > (u.osInstall?.installedAt ?? 0);

/**
 * IS THERE AN OS ON THIS DEVICE AT ALL?
 *
 * Only a till. A card reader's firmware comes from the gateway, not from us,
 * and a cash drawer has no OS whatever — so "no build reported yet" and
 * "Reinstall this till" were, on a payment device, a report about a thing that
 * does not exist and an offer the service refuses outright (`requestReinstall`
 * answers "only a POS terminal has a RubyTill install to redo"). A button the
 * server will always reject is worse than no button.
 *
 * The rule is `osInstallRequired` in shared-types — the same one the service
 * enforces and `lockReason` draws, so this cannot drift away from either.
 * Falls back to the lane test it is defined as, so a stale Vite pre-bundle
 * that dropped the export degrades to the right answer instead of throwing.
 */
const hasOs = (m: DeviceModelRow | undefined): boolean => (
  typeof osInstallRequired === 'function'
    ? osInstallRequired(m as any)
    : deviceLane(m as any) === 'pos'
);

/** The states the service accepts a reinstall from — see `requestReinstall`. */
const canReinstall = (u: DeviceUnitRow, m: DeviceModelRow | undefined): boolean =>
  hasOs(m)
  && !outstandingReinstall(u)
  && (u.state === 'provisioning' || u.state === 'configured' || u.state === 'packing');
import ShimmerImage from '../../components/ShimmerImage';
import { Package } from 'lucide-react';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * THE PROVISIONING PLANS, AS A TREE UNDER THE CONFIGURED STAGE.
 *
 * THE STEPS ARE TICKED HERE. This was a status view — the argument being that
 * a second place to tick would let an order screen mark a device configured
 * that nobody had touched. In practice it made the order screen a dead end:
 * it could say a register was blocked on the OS, show you the exact checklist
 * holding it, and offer no way to work it. People went to the manual state
 * dropdown at the foot of the page and set 'configured' by hand — which skips
 * the checklist entirely and is the very thing the rule was protecting
 * against. Ticking the real steps is the SAFER of the two, so it lives here.
 *
 * The state is still never written directly: the service moves the unit to
 * 'configured' when every required step is done, and back when one is
 * unticked. The checklist IS the flag.
 *
 * The plans already exist — per model, at /inventory/provisioning. What was
 * missing is that nobody working an ORDER could see them: you had to know
 * which devices were on it, open each unit, and check.
 *
 * A TREE, not a list, because the plan belongs to the MODEL and the progress
 * belongs to the DEVICE. A card reader, an Android till and a label printer
 * are three different pieces of work with three different checklists; an order
 * carrying all three has three branches, and repeating each plan once per unit
 * would say the same thing four times and hide that structure.
 *
 *   Dejavoo P17 · payment device · 6 steps
 *     ├ VP3350-B0071   4 of 6
 *     └ VP3350-B0072   not started
 *   TC-TOUCH-K10 · pos terminal · no plan
 *
 * THREE STATES, and the difference matters:
 *   · not started  — the plan is whatever the template says TODAY. Not pinned.
 *   · in progress  — the steps are PINNED to the version worked against, so
 *                    editing the template cannot move this device.
 *   · no plan      — the model has no template. Said plainly, with a way to
 *                    write one, rather than silently passing on an empty list.
 */

interface Props {
  units: DeviceUnitRow[];
  models: DeviceModelRow[];
  /**
   * The ORDER LINES, so the plans show before anything is allocated.
   *
   * Units are the evidence for PROGRESS; the order lines are the evidence for
   * what will need configuring at all. Reading only units meant a fresh order
   * — which is every order, at the moment somebody wants to see what work is
   * coming — showed nothing to configure.
   */
  items?: { modelId?: string; bundleId?: string; quantity?: number }[];
  /**
   * WHICH DEVICES THIS PANEL OWNS.
   *
   * `payment` — the card devices, configured on the merchant lane against the
   * TID from the VAR sheet. `hardware` — everything else, picked and
   * provisioned by the warehouse. Two panels, two sets: without the split,
   * each stage counted the other's work and both read as incomplete.
   */
  only?: 'payment' | 'hardware';
  /**
   * WHICH LANE THIS PANEL IS, by the device category — `deviceLane`.
   *
   * Scopes the MODEL rows, which `unitIds` below cannot: a model on the order
   * has no unit until it is allocated, and an unallocated model was shown on
   * every hardware lane at once. Given, it decides the panel's contents
   * outright; absent, `only` behaves exactly as it always has.
   */
  lane?: DeviceLane;
  /** The unit ids that need a terminal id, so the split matches the resolver's. */
  paymentUnitIds?: readonly string[];
  /**
   * NARROW FURTHER THAN `only`.
   *
   * `only: 'hardware'` means "not a card device", which was the whole answer
   * while the rail had two lanes. It now has four, and the POS lane and the
   * accessories lane both draw this panel — so each passes the exact unit ids
   * it owns. Absent keeps the old behaviour untouched.
   */
  unitIds?: readonly string[];
  /**
   * WHICH STAGE OF THE ORDER THIS PANEL IS MOUNTED AT.
   *
   * Steps are written against the stage they are worked at, so a panel shows
   * only its own — the coupling stage draws the coupling checks, the packing
   * stage the packing instructions. Omitted keeps the whole plan, which is
   * what the configure stages used to be the only place to see.
   */
  stageIds?: readonly string[];
  /**
   * DRAW ONLY THE STEPS, because the caller is already the header.
   *
   * This panel is a TREE: model branch, unit leaf, checklist. That is right
   * when it owns the whole stage, and wrong when it is mounted inside a card
   * that has just drawn the device's picture, barcode, serial and state — the
   * device is then named three times before the first step.
   *
   * `bare` drops the two headers and nothing else. Same data, same ticking,
   * same gates, same OS card; the plan simply renders open, because a
   * disclosure inside a disclosure is a second click to reach work the
   * operator has already opened. Absent, every existing caller draws exactly
   * as it always has.
   */
  bare?: boolean;
  bundles?: { bundleId: string; components?: { modelId: string; quantity?: number }[] }[];
  /**
   * One unit, as the service just returned it.
   *
   * Preferred over `onChanged` for anything that touches a single device:
   * `onChanged` refetches the whole order and remounts the rail, which threw
   * the reader's place away on every tick. The unit comes back from the call
   * that changed it, so there is nothing to refetch.
   */
  onUnitChanged?: (unit: DeviceUnitRow) => void;
  onChanged: () => void;
}

const when = (ts?: number) =>
  ts ? new Date(ts).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

const ProvisioningStagePanel: React.FC<Props> = ({
  units: allUnits, models, items = [], bundles = [], only, lane, paymentUnitIds = [], unitIds, stageIds,
  bare = false, onUnitChanged, onChanged,
}) => {
  // Split before anything else, so every count below is this panel's own.
  const isPayment = (u: { unitId: string }) => paymentUnitIds.includes(u.unitId);
  const byOnly = only === 'payment' ? allUnits.filter(isPayment)
    : only === 'hardware' ? allUnits.filter((u) => !isPayment(u))
    : allUnits;
  // An explicit list wins — the POS lane and the accessories lane are both
  // 'hardware' and would otherwise each show the other's devices.
  const units = unitIds ? byOnly.filter((u) => unitIds.includes(u.unitId)) : byOnly;
  const [starting, setStarting] = useState<string | null>(null);
  /** The unit awaiting a reinstall confirmation, or null. */
  const [reinstalling, setReinstalling] = useState<DeviceUnitRow | null>(null);
  /* Only from the two states the service itself accepts — see
     lifecycle.ts's startProvisioning, which 400s from anywhere else. */
  const canStart = (u: DeviceUnitRow) => u.state === 'allocated' || u.state === 'in_stock';
  const start = async (u: DeviceUnitRow) => {
    setStarting(u.unitId);
    try {
      const res = await hardwareApi.startProvisioning(u.unitId);
      const now = res?.unit?.state;
      const name = u.trackingId || u.serialNumber || u.unitId;
      /* Name the device and the state it landed on. "Provisioning started" on
         a page that then refetches told you nothing about WHICH unit moved or
         where it moved to, which is how starting one device read as the whole
         order having done something. */
      toastService.success(
        now === 'configured'
          ? `${name} — this model has no required steps, so it is already Configured.`
          : `${name} — provisioning started. Tick the steps below as they are done.`,
      );
      // Keep the checklist you just opened in view.
      setOpenUnits((prev) => new Set(prev).add(u.unitId));
      if (res?.unit && onUnitChanged) onUnitChanged(res.unit); else onChanged();
    } catch (e: any) {
      toastService.error(e?.message || 'Could not start provisioning.');
    } finally {
      setStarting(null);
    }
  };
  /*
    TICKING, and what it means.

    The service moves the unit itself: 'provisioning' becomes 'configured' the
    moment every REQUIRED step is done, and reverts if one is unticked. So the
    last tick is the thing that unblocks the register and the packing gate, and
    it is worth saying out loud when it happens rather than leaving someone to
    notice a chip changed somewhere else on the page.
  */
  const [ticking, setTicking] = useState<string | null>(null);
  const tick = async (u: DeviceUnitRow, stepId: string, done: boolean) => {
    setTicking(`${u.unitId}:${stepId}`);
    let was = u.state;
    try {
      /*
        THE FIRST TICK STARTS THE PLAN.

        Provisioning RUNS nothing — it is a checklist, and `startProvisioning`
        only copies the model's template onto the unit so the ticks have
        somewhere to land. Making that a button of its own implied a process
        kicking off, and it read that way: press it and the whole list appears
        at once, unexplained. So it happens on the first tick instead, and
        there is one less thing on the screen that looks like it does
        something it does not.
      */
      if (!u.provisioning) {
        const started = await hardwareApi.startProvisioning(u.unitId);
        if (started?.unit) { was = started.unit.state; onUnitChanged?.(started.unit); }
        setOpenUnits((prev) => new Set(prev).add(u.unitId));
      }
      const res = await hardwareApi.tickProvisioningStep(u.unitId, stepId, done);
      const now = res?.unit?.state;
      if (now === 'configured' && was !== 'configured') {
        toastService.success('Every required step is done — this device is now Configured.');
      } else if (was === 'configured' && now === 'provisioning') {
        toastService.info('A required step was unticked, so this device is back to Provisioning.');
      }
      if (res?.unit && onUnitChanged) onUnitChanged(res.unit); else onChanged();
    } catch (e: any) {
      toastService.error(e?.message || 'Could not update that step.');
    } finally {
      setTicking(null);
    }
  };
  const [templates, setTemplates] = useState<Record<string, ProvisioningTemplate | null>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const dataVersion = useDataVersion();
  /*
    OPEN A FEW AT ONCE, and collapse what you are not working on.

    A twenty-step testing plan across four devices is several screens of
    checklist. One-at-a-time was worse than it sounds — comparing two devices
    meant closing the one you were reading — so these are sets, and the whole
    panel collapses from the header.
  */
  const [openUnits, setOpenUnits] = useState<Set<string>>(new Set());
  const [closedModels, setClosedModels] = useState<Set<string>>(new Set());
  const [openPreview, setOpenPreview] = useState<Set<string>>(new Set());
  /** Which model is choosing a plan to copy, and what there is to copy from. */
  const [copyFor, setCopyFor] = useState<string | null>(null);
  const toggleIn = (set: Set<string>, key: string) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  };
  const [sources, setSources] = useState<{ modelId: string; name: string; steps: number; categories: number }[] | null>(null);
  const [copying, setCopying] = useState<string | null>(null);

  /** What the order CALLS FOR, per model — bundles expanded the same way the allocator expands them. */
  const ordered = new Map<string, number>();
  /*
    WHICH MODELS THIS LANE OWNS.

    `unitIds` narrowed the UNITS to the lane and this narrowed nothing: `only`
    is a two-way split, so both hardware lanes asked for "everything that is not
    a card device" and each listed the other's models. The POS lane drew the
    cash drawer and the USB-C cable beside the till — three model rows on a
    stage whose own summary said "1 POS device" — and the accessories lane drew
    the till. Allocate a unit and it vanished from the wrong lane while its
    model row stayed: the two halves of one panel were scoped differently.

    `lane` is the fix and `deviceLane` is the rule, shared with the rail so the
    model rows and the unit rows can never disagree again. Absent, the old
    two-way behaviour stands untouched for any caller that has not been told
    which lane it is.
  */
  const wants = (modelId: string) => {
    const m = models.find((x) => x.modelId === modelId);
    if (lane) return deviceLane(m as any) === lane;
    if (!only) return true;
    return needsTerminalId(m as any) === (only === 'payment');
  };
  for (const it of items) {
    const qty = it.quantity || 1;
    if (it.bundleId) {
      const b = bundles.find((x) => x.bundleId === it.bundleId);
      for (const c of b?.components || []) {
        if (wants(c.modelId)) ordered.set(c.modelId, (ordered.get(c.modelId) || 0) + (c.quantity || 1) * qty);
      }
    } else if (it.modelId && wants(it.modelId)) {
      ordered.set(it.modelId, (ordered.get(it.modelId) || 0) + qty);
    }
  }

  // Every model on the order, allocated or not.
  const modelIds = Array.from(new Set([
    ...units.map((u) => u.modelId).filter(Boolean),
    ...ordered.keys(),
  ]));

  const load = useCallback(async () => {
    const entries = await Promise.all(modelIds.map(async (modelId) => {
      try {
        const res = await hardwareApi.getProvisioningTemplate(modelId);
        // A template with no steps is not a plan — treat it as none, so the
        // screen says so instead of showing an empty checklist as complete.
        const t = res.template;
        return [modelId, t && (t.steps || []).length > 0 ? t : null] as const;
      } catch {
        return [modelId, null] as const;
      }
    }));
    setTemplates(Object.fromEntries(entries));
    setLoading(false);
  }, [modelIds.join(','), dataVersion]); // eslint-disable-line react-hooks/exhaustive-deps -- dataVersion: re-read after any save

  useEffect(() => { load(); }, [load]);

  /**
   * WHAT THERE IS TO COPY FROM.
   *
   * There is no list-templates endpoint, so this asks per model — lazily, only
   * when somebody opens the picker, and never on page load. Most estates have
   * a couple of dozen models; a handful of parallel reads on a deliberate
   * click is a fair trade against a new endpoint and a deploy.
   */
  const loadSources = useCallback(async (exceptModelId: string) => {
    setSources(null);
    const others = models.filter((m) => m.modelId !== exceptModelId);
    const found = await Promise.all(others.map(async (m) => {
      try {
        const res = await hardwareApi.getProvisioningTemplate(m.modelId);
        const t = res.template;
        // The endpoint synthesizes an empty default rather than 404ing, so an
        // empty step list means "no plan", not "a plan with nothing in it".
        if (!t || (t.steps || []).length === 0) return null;
        return {
          modelId: m.modelId,
          name: `${m.manufacturer} ${m.modelName}`,
          steps: t.steps.length,
          categories: (t.categories || []).length,
        };
      } catch {
        return null;
      }
    }));
    setSources(found.filter(Boolean) as any);
  }, [models]);

  /**
   * Copy a plan onto this model.
   *
   * It is a COPY, not a link: the two models drift apart the first time one of
   * them needs an extra step, and a shared plan would drag that change onto
   * hardware nobody was thinking about. Categories and steps only — nothing
   * model-specific travels.
   */
  const copyPlan = async (toModelId: string, fromModelId: string) => {
    setCopying(fromModelId);
    try {
      const { template } = await hardwareApi.getProvisioningTemplate(fromModelId);
      await hardwareApi.putProvisioningTemplate(toModelId, {
        categories: template.categories || [],
        steps: (template.steps || []).map((st) => ({
          stepId: st.stepId,
          label: st.label,
          categoryId: st.categoryId,
          required: st.required,
          order: st.order,
          instructions: st.instructions,
        })),
      });
      setCopyFor(null);
      setSources(null);
      await load();
      toastService.success('Plan copied. Edit it on this model whenever it needs to differ.');
    } catch (e: any) {
      toastService.error(e?.message || 'Could not copy that plan.');
    } finally {
      setCopying(null);
    }
  };

  if (loading) {
    return <RubyLoader variant="inline" label="reading the plans…" />;
  }

  if (modelIds.length === 0) {
    return (
      <p className="text-[12.5px] text-stone-500">
        {only === 'payment' ? 'No card devices on this order.'
          : only === 'hardware' ? 'No other hardware on this order.'
          : 'No devices on this order.'}
      </p>
    );
  }

  const CATEGORY_LABEL: Record<string, string> = {
    payment_device: 'payment device',
    pos_terminal: 'POS terminal',
    peripheral: 'peripheral',
    accessory: 'accessory',
  };

  /*
    ONLY THIS STAGE'S WORK.

    Every step is filed under the order stage it is worked at, so a stage
    panel narrows to its own — the plan preview, the pinned snapshot and every
    count derived from either. Filtering here at the source is what keeps
    "6 steps in the plan", the progress badge and the list itself from
    describing three different sets.

    A pinned step carries its group's LABEL rather than its id (that is what
    the service snapshots), so the model resolves it back to a stage; the
    device's lane settles the two lanes that share the label "Configure
    hardware". No `stageIds` means the whole plan, as before.
  */
  const inStage = (stepStageId: string | undefined) =>
    !stageIds || (!!stepStageId && stageIds.includes(stepStageId));

  /** The OS install is the POS configure stage's own gate — see its use below. */
  const showOsCard = !stageIds || stageIds.includes('provisioned');

  const visibleSnapshotSteps = (u: DeviceUnitRow, model: DeviceModelRow | undefined) =>
    (u.provisioning?.steps || []).filter((st) => inStage(stageForSnapshotLabel(st.category || '', model)?.id));

  // One branch per model, in the order the devices appear on the order.
  const branches = modelIds.map((modelId) => {
    const mine = units.filter((u) => u.modelId === modelId);
    const model = models.find((m) => m.modelId === modelId);
    const full = templates[modelId];
    return {
      modelId,
      model,
      template: full
        ? { ...full, steps: (full.steps || []).filter((st) => inStage(st.categoryId)) }
        : full,
      units: mine,
      /** How many the order wants — the denominator before anything is allocated. */
      expected: ordered.get(modelId) ?? mine.length,
    };
  })
    /*
      A DEVICE WITH NOTHING TO DO AT THIS STAGE IS NOT DRAWN.

      The point of binding steps to stages is that the coupling stage shows
      coupling work — listing every device on the order under it again, each
      with an empty list, is the pile this replaced.

      EXCEPT a model with NO PLAN AT ALL, on a configure stage. That device is
      not "nothing to do here" — it is "nothing to do it WITH", and its card
      (write a plan / copy one / mark configured) is the only way to start the
      unit from the order. Hiding it left the lane reading "no checklist step"
      while its units sat in `allocated` forever.
    */
    .filter((b) => !stageIds
      || (b.template?.steps || []).length > 0
      || b.units.some((u) => visibleSnapshotSteps(u, b.model).length > 0)
      || (!templates[b.modelId] && stageIds.some((id) => id !== 'allocated' && CONFIGURED_STAGE_IDS.includes(id))));

  if (stageIds && branches.length === 0) {
    return (
      <p className="text-[12.5px] text-stone-500">
        No device on this order has a checklist step at this stage.
      </p>
    );
  }

  const anyOpen = closedModels.size < branches.length || openUnits.size > 0 || openPreview.size > 0;

  return (
    <div className="space-y-3">
      {/*
        ONE CONTROL for the whole tree. With a twenty-step plan across several
        devices, scrolling past what you are NOT working on is most of the
        interaction — so it folds away from here.
      */}
      {branches.length > 1 && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-[11px] text-stone-400">
            {branches.length} models · {branches.reduce((n, b) => n + b.expected, 0)} devices
          </span>
          <button
            onClick={() => {
              if (anyOpen) {
                setClosedModels(new Set(branches.map((b) => b.modelId)));
                setOpenUnits(new Set());
                setOpenPreview(new Set());
              } else {
                setClosedModels(new Set());
                setOpenUnits(new Set(units.map((u) => u.unitId)));
                setOpenPreview(new Set(branches.filter((b) => b.template).map((b) => b.modelId)));
              }
            }}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-stone-200 bg-white text-[11px] font-semibold text-stone-600 hover:bg-stone-50"
          >
            {anyOpen ? <ChevronsDownUp size={11} /> : <ChevronsUpDown size={11} />}
            {anyOpen ? 'Collapse all' : 'Expand all'}
          </button>
        </div>
      )}

      {branches.map((b) => {
        const configured = b.units.filter((u) => {
          const st = u.provisioning?.steps || [];
          return st.length > 0 && st.filter((x) => x.required).every((x) => x.done);
        }).length;

        return (
          <div
            key={b.modelId}
            className={bare ? '' : 'rounded-xl border border-stone-200 bg-white overflow-hidden'}
          >
            {/* ── the branch: the MODEL and its plan ──
                Not drawn bare: the card this is mounted in has already said
                which model and which device this is. */}
            {!bare && (
            <button
              onClick={() => setClosedModels((c) => toggleIn(c, b.modelId))}
              className="w-full text-left px-3.5 py-3 bg-stone-50 border-b border-stone-200 hover:bg-stone-100/70"
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex items-start gap-3">
                  {/* THE DEVICE, AS A PICTURE. Reading
                      "TC-TOUCH-K10-WHITE-DUAL-TOUCH" and knowing what is in
                      the box are different things — and the person reading
                      this is often the one holding it. */}
                  <ShimmerImage
                    src={b.model?.media?.heroImageUrl}
                    alt={b.model?.modelName || b.modelId}
                    width={40}
                    height={40}
                    objectFit="contain"
                    fallbackIcon={<Package size={16} className="text-gray-400" />}
                    fallbackBgColor="bg-gray-100"
                    variant="light"
                  />
                  <div className="min-w-0">
                  {/* Wraps for the same reason the allocate panel does. */}
                  <p className="text-[13px] font-semibold text-stone-900 leading-snug break-words">
                    {b.model ? `${b.model.manufacturer} ${b.model.modelName}` : b.modelId}
                  </p>
                  <p className="text-[11.5px] text-stone-500 mt-0.5">
                    {CATEGORY_LABEL[b.model?.category || ''] || 'device'}
                    {' · '}
                    {b.template
                      ? `${b.template.steps.length} step${b.template.steps.length === 1 ? '' : 's'} in the plan`
                      : 'no plan'}
                    {' · '}{b.expected} on this order
                  </p>
                  </div>
                </div>
                <span className="flex items-center gap-1.5 shrink-0">
                  <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${
                    configured === b.expected ? 'bg-emerald-100 text-emerald-700'
                    : b.template ? 'bg-amber-100 text-amber-700' : 'bg-gray-200 text-gray-700'}`}
                  >
                    {configured} of {b.expected} configured
                  </span>
                  <ChevronDown
                    size={13}
                    className={`text-stone-400 transition-transform ${closedModels.has(b.modelId) ? '-rotate-90' : ''}`}
                  />
                </span>
              </div>

              {/* NO PLAN — said plainly, with the way to write one. */}
            </button>
            )}

            {!b.template && !closedModels.has(b.modelId) && (
              <div className="px-3.5 py-3">
                <div className="rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2.5">
                  <p className="text-[12px] text-stone-700 leading-relaxed">
                    <b>No plan for this model.</b> A device configured against an empty checklist reads as
                    "checked" and is not.
                  </p>

                  <div className="flex flex-wrap gap-2 mt-2">
                    <button
                      onClick={() => { setCopyFor(copyFor === b.modelId ? null : b.modelId); if (copyFor !== b.modelId) loadSources(b.modelId); }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-[11px] font-semibold"
                    >
                      <Copy size={11} /> Copy from another device
                    </button>
                    <a
                      href={`#/inventory/provisioning?model=${encodeURIComponent(b.modelId)}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-stone-200 text-stone-700 hover:bg-stone-50 text-[11px] font-semibold"
                    >
                      <ClipboardList size={11} /> Write one <ExternalLink size={10} />
                    </a>
                  </div>

                  {/*
                    STARTING A MODEL WITH NO PLAN.

                    The service takes it: an empty checklist's zero required
                    steps are vacuously satisfied, so the unit lands straight
                    on 'configured'. That is the state the register and the
                    packing gate are waiting for, so refusing to offer it here
                    would leave those two stages stuck with no way forward and
                    no explanation — while the warning above still says, at
                    full strength, that a device checked against nothing has
                    not been checked.
                  */}
                  {b.units.some((u: DeviceUnitRow) => canStart(u)) && (
                    <div className="mt-2.5 border-t border-amber-200 pt-2.5">
                      <p className="text-[11.5px] text-stone-600 mb-1.5">
                        With no steps to tick, there is nothing to work through — marking a device here sets it
                        straight to Configured.
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {/*
                          THE ROUTE THAT USED TO BYPASS EVERYTHING.

                          A model with no checklist auto-advances to
                          'configured' the moment this is pressed, so on a till
                          it was one click from "no OS, no plan, ready to pack".
                          The service refuses it now; this button has to say so
                          rather than offering an action that 409s.
                        */}
                        {b.units.filter((u: DeviceUnitRow) => canStart(u)).map((u: DeviceUnitRow) => {
                          const unitLocked = lockReason(u, b.model);
                          return (
                            <button
                              key={u.unitId}
                              onClick={() => start(u)}
                              disabled={!!unitLocked || starting === u.unitId}
                              title={unitLocked || undefined}
                              className="inline-flex min-h-[34px] items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-2.5 text-[11px] font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-50"
                            >
                              {starting === u.unitId ? <RubyGemMark size={11} drawing color="currentColor" />
                                : unitLocked ? <Lock size={11} className="text-amber-600" />
                                : <Check size={11} />}
                              <span>
                                {unitLocked ? 'OS needed — ' : 'Mark '}
                                <span className="font-mono">{u.trackingId || u.serialNumber || u.unitId}</span>
                                {unitLocked ? '' : ' configured'}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {copyFor === b.modelId && (
                    <div className="mt-2.5">
                      {sources === null && (
                        <RubyLoader variant="inline" label="looking for plans to copy…" />
                      )}
                      {sources?.length === 0 && (
                        <p className="text-[11.5px] text-stone-600">
                          No other model has a plan yet. This one has to be written first — after that it can
                          be copied onto the rest.
                        </p>
                      )}
                      <div className="space-y-1">
                        {sources?.map((src) => (
                          <button
                            key={src.modelId}
                            onClick={() => copyPlan(b.modelId, src.modelId)}
                            disabled={!!copying}
                            className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg border border-stone-200 bg-white hover:bg-stone-50 text-left disabled:opacity-50"
                          >
                            {copying === src.modelId
                              ? <RubyGemMark size={11} drawing color="currentColor" />
                              : <Copy size={11} className="text-stone-400 shrink-0" />}
                            <span className="text-[12px] text-stone-800 flex-1 min-w-0 break-words">{src.name}</span>
                            <span className="text-[11px] text-stone-400 shrink-0">
                              {src.steps} step{src.steps === 1 ? '' : 's'}
                            </span>
                          </button>
                        ))}
                      </div>
                      {!!sources?.length && (
                        <p className="text-[11px] text-stone-400 mt-1.5 leading-relaxed">
                          Copied, not linked — edit it here whenever this device needs to differ, without
                          touching the one you copied from.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Nothing allocated yet — the work that is coming, COLLAPSED by default. */}
            {b.units.length === 0 && b.template && !closedModels.has(b.modelId) && (
              <div className="px-3.5 py-3">
                <p className="text-[12px] text-stone-500 leading-relaxed">
                  Not allocated yet. Each of the {b.expected} device{b.expected === 1 ? '' : 's'} will get this
                  checklist, pinned when work begins.
                </p>
                <button
                  onClick={() => setOpenPreview((o) => toggleIn(o, b.modelId))}
                  className="inline-flex items-center gap-1.5 mt-1.5 text-[11.5px] font-semibold text-stone-600 hover:text-stone-900"
                >
                  <ChevronDown size={12} className={`transition-transform ${openPreview.has(b.modelId) ? '' : '-rotate-90'}`} />
                  {openPreview.has(b.modelId) ? 'Hide' : 'Show'} the {b.template.steps.length} steps
                </button>
                {openPreview.has(b.modelId) && (
                  <div className="space-y-1 mt-2">
                    {b.template.steps.map((st) => (
                      <div key={st.stepId} className="flex items-start gap-2.5 px-2.5 py-1.5 rounded-lg border border-stone-200 bg-stone-50">
                        <span className="w-[15px] h-[15px] rounded-[4px] border border-stone-300 bg-white shrink-0 mt-[2px]" />
                        <span className="min-w-0">
                          <span className="block text-[12px] text-stone-600">
                            {st.label}{!st.required && <span className="text-stone-400"> · optional</span>}
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── the leaves: each DEVICE and its own progress ── */}
            {!closedModels.has(b.modelId) && (
            <div className="divide-y divide-stone-100">
              {b.units.map((u) => {
                const snapshot = u.provisioning;
                const steps = visibleSnapshotSteps(u, b.model);
                const required = steps.filter((x) => x.required);
                const doneRequired = required.filter((x) => x.done);
                const complete = steps.length > 0 && required.every((x) => x.done);
                /* Bare means the caller already opened this device — there is
                   no header here to open it with, so it renders open. */
                const open = bare || openUnits.has(u.unitId);

                /*
                  WHAT THE DEVICE ITSELF REPORTED.

                  The install is triggered from device management, and the
                  build it landed on comes back on the unit (`osVersion`,
                  `appBuild`, `lastSeenAt`). Showing it here is what lets
                  somebody tick "RubyTill installed" against a FACT instead of
                  against their memory of having pressed a button on another
                  screen. Absent means the device has not reported in — said
                  as that, never as a zero.
                */
                const build = [u.osVersion, u.appBuild].filter(Boolean).join(' · ');

                /*
                  THE INSTALL GATE, on this device.

                  `locked` is the reason the checklist below cannot be worked
                  yet, and it is the SAME rule the service applies — so every
                  row this greys out is a row the server would have refused.
                  The stamp is what clears it, and only a real install writes
                  one.
                */
                const locked = lockReason(u, b.model);
                const gated = !!locked || !!u.osInstall;

                return (
                  <div key={u.unitId} className={bare ? '' : 'px-3.5 py-2.5'}>
                    {!bare && (
                    <button
                      onClick={() => setOpenUnits((o) => toggleIn(o, u.unitId))}
                      className="w-full flex items-center gap-2.5 text-left"
                    >
                      <span className="text-stone-300 select-none shrink-0">└</span>
                      <span className="flex-1 min-w-0">
                        <span className="block font-mono text-[12.5px] text-stone-700 truncate">
                          {u.trackingId || u.serialNumber || u.unitId}
                        </span>
                        {/*
                          The build line is about the OS, so it is drawn only
                          where there is one. A payment device read "no build
                          reported yet" for ever — a permanent absence reported
                          as a pending one. Its serial is the useful line there.
                        */}
                        <span className="block text-[11px] truncate">
                          {build ? (
                            <span className="text-emerald-700">
                              {build}
                              {u.lastSeenAt ? <span className="text-stone-400"> · last seen {when(u.lastSeenAt)}</span> : null}
                            </span>
                          ) : hasOs(b.model) ? (
                            <span className="text-stone-400">no build reported yet</span>
                          ) : u.serialNumber ? (
                            <span className="text-stone-400">serial {u.serialNumber}</span>
                          ) : null}
                        </span>
                      </span>
                      {/*
                        THE LOCK READS BEFORE THE ROW IS OPENED.

                        "Not started" and "blocked on the OS" are different
                        situations with different next actions, and a device
                        showing the first while it means the second sends
                        somebody to tick a checklist that will refuse them.
                        This badge sits where the progress count would, and
                        the count comes back the moment the gate clears.
                      */}
                      {locked && (
                        <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-100 text-amber-800 shrink-0 inline-flex items-center gap-1">
                          <Lock size={9} /> OS not installed
                        </span>
                      )}
                      {!locked && snapshot
                        ? (
                          <span className={`px-2 py-0.5 rounded text-[11px] font-semibold shrink-0 ${
                            complete ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}
                          >
                            {doneRequired.length} of {required.length}
                          </span>
                        )
                        : !locked && <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-gray-200 text-gray-700 shrink-0">Not started</span>}
                      {(snapshot || b.template || gated) && (
                        <ChevronDown size={13} className={`text-stone-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
                      )}
                    </button>
                    )}

                    {/*
                      SEND IT BACK, from the order it belongs to.

                      Offered outside the collapsible header because that
                      header is itself a button and a nested one is not valid.
                      Shown only for a device that HAS been built — a unit that
                      never got past 'allocated' has nothing to redo, and the
                      service refuses it with exactly that reason.
                    */}
                    {!hasOs(b.model) ? null : outstandingReinstall(u) ? (
                      <p className={`mt-1.5 ${bare ? '' : 'ml-5'} inline-flex items-start gap-1.5 text-[11.5px] leading-relaxed text-amber-800`}>
                        <RotateCcw size={11} className="mt-0.5 shrink-0" />
                        <span>
                          Reinstall requested {when(u.reinstall!.requestedAt)} by {u.reinstall!.requestedBy}
                          {u.reinstall!.reason ? <> — {u.reinstall!.reason}</> : null}. It is waiting in Pending Configuration.
                        </span>
                      </p>
                    ) : canReinstall(u, b.model) ? (
                      <div className={bare ? "mt-1.5" : "mt-1.5 ml-5"}>
                        <button
                          onClick={() => setReinstalling(u)}
                          className="inline-flex min-h-[34px] items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-2.5 text-[11px] font-semibold text-stone-600 hover:border-amber-300 hover:bg-amber-50 hover:text-amber-800"
                        >
                          <RotateCcw size={11} /> Reinstall this till
                        </button>
                      </div>
                    ) : null}

                    {/* The OS gate, drawn by the SHARED card — the same one
                        the Pending configuration list at /pos-config uses.

                        IT BELONGS TO ONE STAGE. The install is part of
                        configuring the till, so a stage-scoped panel draws it
                        only there: above a packing instruction it is neither
                        the work being asked for nor the thing blocking it. */}
                    {open && gated && showOsCard && (
                      <div className={bare ? "mt-2" : "mt-2 ml-5"}>
                        <OsInstallCard
                          unit={u}
                          model={b.model}
                          stepCount={steps.length || b.template?.steps.length || 0}
                        />
                      </div>
                    )}

                    {open && !snapshot && b.template && (
                      <div className={bare ? "mt-2" : "mt-2 ml-5"}>
                        <p className="text-[12px] text-stone-600 leading-relaxed mb-1.5">
                          {locked
                            ? 'Locked until the OS is on this device. Nothing here can be ticked yet — the service refuses it too, so this is not a display rule you can work around.'
                            : 'Not started. Tick the first step and the plan is pinned to this device, so a later edit to the model\'s template cannot move it.'}
                        </p>
                        {/*
                          STARTING IS NOT TICKING.

                          This panel stays a status view — nobody marks a step
                          done from an order screen, because the person with
                          the device in their hands is the only one who knows.
                          But STARTING asserts no work at all: it snapshots the
                          model's plan and moves the unit to 'provisioning'.
                          Without it the order screen could tell you a register
                          was blocked on the OS and give you nowhere to go.

                          A model with no template auto-advances straight to
                          'configured' here — zero required steps are
                          vacuously satisfied — which is said out loud below
                          rather than left as a surprise.
                        */}
                        <div className="space-y-1">
                          {b.template.steps.map((st) => (
                            <button
                              key={st.stepId}
                              type="button"
                              onClick={() => tick(u, st.stepId, true)}
                              disabled={!!locked || !canStart(u) || ticking === `${u.unitId}:${st.stepId}`}
                              title={locked || undefined}
                              className="w-full text-left flex items-start gap-2.5 px-2.5 py-2 min-h-[44px] rounded-lg border border-stone-200 bg-white hover:bg-stone-50 disabled:opacity-60 disabled:hover:bg-white"
                            >
                              <span className="w-[16px] h-[16px] rounded-[4px] border border-stone-300 bg-white grid place-items-center shrink-0 mt-[2px]">
                                {ticking === `${u.unitId}:${st.stepId}`
                                  ? <RubyGemMark size={9} drawing color="currentColor" />
                                  : null}
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block text-[12.5px] text-stone-800">
                                  {st.label}
                                  <span className="text-stone-400">{st.required ? ' · required' : ' · optional'}</span>
                                </span>
                                {st.instructions && (
                                  <span className="block text-[11px] text-stone-500 mt-0.5 leading-relaxed">{st.instructions}</span>
                                )}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {open && snapshot && (
                      <div className={bare ? "mt-2 space-y-1" : "mt-2 ml-5 space-y-1"}>
                        {/*
                          THE PLAN MOVED ON WITHOUT THIS DEVICE.

                          A unit pins the plan the moment provisioning starts,
                          so steps added to a stage afterwards are not on it —
                          and a stage-scoped view of such a unit is simply
                          empty, which reads as "nothing to do here" when the
                          truth is "this device never got these". The service
                          would refuse a tick against a step its snapshot does
                          not carry, so they are SAID rather than drawn as
                          checkboxes that cannot be ticked.
                        */}
                        {stageIds && steps.length === 0 && (b.template?.steps || []).length > 0 && (
                          <p className="text-[11px] leading-relaxed text-amber-700">
                            This device started on an earlier plan, so the{' '}
                            {b.template!.steps.length} step{b.template!.steps.length === 1 ? '' : 's'} since
                            added to this stage are not on it:{' '}
                            {b.template!.steps.map((st) => st.label).join(' · ')}.
                          </p>
                        )}
                        {steps.map((st) => (
                          <button
                            key={st.stepId}
                            type="button"
                            onClick={() => tick(u, st.stepId, !st.done)}
                            /* Locked blocks TICKING, never UNticking — the
                               service draws the line in the same place. A
                               correction made after the lock appeared must
                               not need an install to undo, or a wrong tick
                               becomes permanent. */
                            disabled={(!!locked && !st.done) || ticking === `${u.unitId}:${st.stepId}`}
                            title={!st.done ? (locked || undefined) : undefined}
                            aria-pressed={!!st.done}
                            className={`w-full text-left flex items-start gap-2.5 px-2.5 py-2 min-h-[44px] rounded-lg border transition-colors disabled:opacity-60 ${
                              st.done ? 'border-emerald-200 bg-emerald-50/60 hover:bg-emerald-50'
                              : 'border-stone-200 bg-white hover:bg-stone-50'}`}
                          >
                            {/*
                              AN UNDONE STEP IS NOT A FAILED ONE.

                              A required step used to render a RED CROSS on a
                              red row, which is the vocabulary of something
                              that went wrong — a plan nobody had started yet
                              looked like a device full of faults. Undone is
                              now simply an empty box; "required" is said in
                              words beside the label, where it belongs.
                            */}
                            <span className={`w-[16px] h-[16px] rounded-[4px] grid place-items-center text-[10px] text-white shrink-0 mt-[2px] ${
                              st.done ? 'bg-emerald-500' : 'border border-stone-300 bg-white'}`}
                            >
                              {ticking === `${u.unitId}:${st.stepId}`
                                ? <RubyGemMark size={9} drawing color="currentColor" />
                                : st.done ? <Check size={9} strokeWidth={3} /> : null}
                            </span>
                            <span className="min-w-0 flex-1">
                              {/* A done step is TICKED, not crossed out. Striking
                                  the text says "this no longer applies", which is
                                  the opposite of what a finished check means — and
                                  it made the completed half of a plan the hardest
                                  half to read. The green tick carries it. */}
                              <span className="block text-[12.5px] text-stone-800">
                                {st.label}
                                {st.required
                                  ? <span className="text-stone-400"> · required</span>
                                  : <span className="text-stone-400"> · optional</span>}
                              </span>
                              <span className="block text-[11px] text-stone-400">
                                {st.category}
                                {st.done && st.by ? ` · ${st.by}` : ''}
                                {st.done && st.at ? ` · ${when(st.at)}` : ''}
                              </span>
                              {st.instructions && !st.done && (
                                <span className="block text-[11px] text-stone-500 mt-0.5 leading-relaxed">{st.instructions}</span>
                              )}
                            </span>
                          </button>
                        ))}

                        <p className="text-[11px] text-stone-400 pt-1 leading-relaxed">
                          Started {when(snapshot.startedAt)}
                          {(snapshot as any).templateSynthesized
                            ? ' · against a synthesized plan — nobody wrote a checklist for this model'
                            : (snapshot as any).templateUpdatedAt
                              ? ` · plan of ${when((snapshot as any).templateUpdatedAt)}`
                              : ''}
                          {b.template && (snapshot as any).templateUpdatedAt
                            && b.template.updatedAt !== (snapshot as any).templateUpdatedAt && (
                            <span className="text-amber-700">
                              {' '}· the model’s plan has changed since; this device keeps the one it started on
                            </span>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            )}
          </div>
        );
      })}

      {branches.some((b) => !b.template) && (
        <div className="flex items-start gap-2 text-[11.5px] text-stone-500 leading-relaxed">
          <AlertTriangle size={13} className="text-amber-500 shrink-0 mt-0.5" />
          A device with no plan can still be packed — the packing gate holds a device whose REQUIRED steps are
          unticked, and an empty checklist has none. Writing the plan is what makes this stage mean anything.
        </div>
      )}

      {reinstalling && (
        <ReinstallModal
          unitId={reinstalling.unitId}
          label={reinstalling.trackingId || reinstalling.serialNumber || reinstalling.unitId}
          fromState={reinstalling.state}
          ticked={(reinstalling.provisioning?.steps || []).filter((st) => st.done).length}
          onClose={() => setReinstalling(null)}
          onDone={(unit) => { if (onUnitChanged) onUnitChanged(unit); else onChanged(); }}
        />
      )}
    </div>
  );
};

export default ProvisioningStagePanel;
